/**
 * Subscription Price Updater
 * Custom integration for Shopify Subscriptions that properly handles sale prices
 */

class SubscriptionPriceUpdater {
  constructor(container) {
    this.container = container;
    this.subscriptionContainer = container.querySelector('.shopify_subscriptions_app_container');
    this.lastUpdateTime = 0;
    this.updateDebounceDelay = 100; // ms

    // Store observers for cleanup
    this.sellingPlanObservers = [];

    // Cache for price element selectors (invalidated on variant change)
    this.cachedPriceElements = null;

    if (!this.subscriptionContainer) return;

    this.init();
  }

  init() {
    // Store original product prices for reference
    this.storeOriginalPrices();

    // Listen for variant changes to update stored prices
    this._onVariantPricesUpdated = this._onVariantPricesUpdated.bind(this);
    this.container.addEventListener('theme:variant:prices-updated', this._onVariantPricesUpdated);

    // Wait for the Shopify widget to initialize
    this.waitForWidget().then(() => {
      this.attachEventListeners();
      this.observeSellingPlanInputChanges();

      // Check if a plan is already selected
      const selectedOption = this.getSelectedPurchaseOption();
      if (selectedOption) {
        this.handleSellingPlanChange(selectedOption);
      }
    });
  }

  storeOriginalPrices() {
    // Read prices from data attributes on the product form (always present, unlike price block)
    const formWrapper = this.container.querySelector('[data-form-wrapper]');

    if (!formWrapper) {
      this.originalPrice = 0;
      this.originalCompareAtPrice = 0;
      this.isProductOnSale = false;
      return;
    }

    // Read variant price from data attribute (in cents)
    const variantPrice = formWrapper.dataset.variantPrice;
    this.originalPrice = variantPrice ? parseInt(variantPrice, 10) : 0;

    // Read compare price from data attribute (in cents)
    const variantComparePrice = formWrapper.dataset.variantComparePrice;
    this.originalCompareAtPrice = variantComparePrice ? parseInt(variantComparePrice, 10) : 0;

    // Check if product is originally on sale
    this.isProductOnSale = this.originalCompareAtPrice && this.originalCompareAtPrice > this.originalPrice;
  }

  _onVariantPricesUpdated() {
    // Invalidate cached selectors since product-info.js replaced the DOM
    this.invalidatePriceElementsCache();
    this.storeOriginalPrices();
    this.reInitializeWidget();
  }

  reInitializeWidget() {
    // Wait for widget to be ready again
    this.waitForWidget().then(() => {
      // Re-attach event listeners to new radio buttons
      this.attachEventListeners();
      this.observeSellingPlanInputChanges();

      // Update prices if a plan is already selected
      const selectedOption = this.getSelectedPurchaseOption();
      if (selectedOption) {
        this.handleSellingPlanChange(selectedOption);
      }
    });
  }

  extractPrice(priceText) {
    // Extract numeric price from formatted string like "$45.00", "€31,46 EUR", "45,00 €", etc.
    if (!priceText) return 0;

    const priceString = priceText.toString().trim();

    // Remove currency symbols and codes (USD, EUR, GBP, etc.)
    let cleaned = priceString
      .replace(/[A-Z]{3}/g, '') // Remove currency codes like USD, EUR, GBP
      .replace(/[$€£¥₹₽¢₩₪₦₨]/g, '') // Remove common currency symbols
      .trim();

    // Determine decimal separator by looking at the last comma or period
    // In formats like "1.234,56" the comma is decimal, in "1,234.56" the period is decimal
    const lastComma = cleaned.lastIndexOf(',');
    const lastPeriod = cleaned.lastIndexOf('.');

    let decimalSeparator;
    let thousandsSeparator;

    if (lastComma > lastPeriod) {
      // Comma is the decimal separator (European format: 1.234,56)
      decimalSeparator = ',';
      thousandsSeparator = '.';
    } else {
      // Period is the decimal separator (US format: 1,234.56)
      decimalSeparator = '.';
      thousandsSeparator = ',';
    }

    // Remove thousands separators and normalize decimal separator to period
    cleaned = cleaned.replace(new RegExp('\\' + thousandsSeparator, 'g'), '').replace(decimalSeparator, '.');

    // Remove any remaining non-numeric characters except decimal point
    cleaned = cleaned.replace(/[^0-9.]/g, '');

    const price = parseFloat(cleaned);

    if (isNaN(price)) return 0;

    return Math.round(price * 100); // Convert to cents and round to avoid floating point issues
  }

  waitForWidget() {
    return new Promise((resolve) => {
      const radioButtons = this.getSellingPlanRadioButtons();
      if (radioButtons.length > 0) {
        resolve();
        return;
      }

      const observer = new MutationObserver(() => {
        const radios = this.getSellingPlanRadioButtons();
        if (radios.length > 0) {
          observer.disconnect();
          resolve();
        }
      });

      observer.observe(this.container, {
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 5000);
    });
  }

  getSellingPlanRadioButtons() {
    return this.container.querySelectorAll('.shopify_subscriptions_app_block input[type="radio"]');
  }

  getSelectedPurchaseOption() {
    return this.container.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden) input[type="radio"]:checked');
  }

  attachEventListeners() {
    const radioButtons = this.getSellingPlanRadioButtons();

    radioButtons.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        this.handleSellingPlanChange(e.target);
      });
    });
  }

  observeSellingPlanInputChanges() {
    // Disconnect any existing selling plan observers before creating new ones
    this.disconnectSellingPlanObservers();

    const sellingPlanInputs = this.container.querySelectorAll('.selected-selling-plan-id');

    sellingPlanInputs.forEach((input) => {
      const observer = new MutationObserver(() => {
        const selectedOption = this.getSelectedPurchaseOption();
        if (selectedOption) {
          this.updatePrices(selectedOption);
        }
      });

      observer.observe(input, {
        attributes: true,
        attributeFilter: ['value'],
      });

      // Store observer reference for cleanup
      this.sellingPlanObservers.push(observer);
    });
  }

  handleSellingPlanChange(radioButton) {
    this.updatePrices(radioButton);
    this.dispatchCustomEvent(radioButton);
  }

  updatePrices(radioButton) {
    // Debounce to prevent duplicate calls
    const now = Date.now();
    const planId = radioButton.dataset.sellingPlanId;

    // Skip if same plan was updated very recently (within debounce delay)
    if (now - this.lastUpdateTime < this.updateDebounceDelay && this.lastPlanId === planId) {
      return;
    }

    this.lastUpdateTime = now;
    this.lastPlanId = planId;

    const subscriptionPriceStr = radioButton.dataset.variantPrice;
    const subscriptionCompareStr = radioButton.dataset.variantCompareAtPrice;
    const sellingPlanId = radioButton.dataset.sellingPlanId;
    const sellingPlanAdjustment = radioButton.dataset.sellingPlanAdjustment;

    if (!subscriptionPriceStr) {
      return;
    }

    // Check if price is already in cents (number) or formatted string
    let subscriptionPrice;
    let subscriptionComparePrice = null;

    // Check if it's a formatted string with currency symbols or letters (not just a plain number)
    const isFormattedString = typeof subscriptionPriceStr === 'string' && /[^0-9]/.test(subscriptionPriceStr);

    if (isFormattedString) {
      // It's a formatted string like "$43.92 USD", "€31,46 EUR", "45,00 €", etc.
      subscriptionPrice = this.extractPrice(subscriptionPriceStr);
      if (subscriptionCompareStr) {
        subscriptionComparePrice = this.extractPrice(subscriptionCompareStr);
      }
    } else {
      // It's already in cents (plain number or numeric string)
      subscriptionPrice = parseInt(subscriptionPriceStr, 10);
      subscriptionComparePrice = subscriptionCompareStr ? parseInt(subscriptionCompareStr, 10) : null;
    }

    if (isNaN(subscriptionPrice)) {
      return;
    }

    // Determine which prices to display based on logic:
    // 1. If product is on sale AND subscription adds discount: show subscription price + original sale price
    // 2. If product is on sale BUT subscription doesn't add discount: show original sale price + original compare price
    // 3. If product not on sale AND subscription adds discount: show subscription price + original price
    // 4. If product not on sale AND subscription doesn't add discount: show original price only

    let displayPrice = subscriptionPrice;
    let displayComparePrice = null;
    let showAsSale = false;

    // Check if the subscription plan actually changes the price
    const planChangesPrice = subscriptionPrice !== this.originalPrice;

    if (this.isProductOnSale) {
      // Product already on sale
      if (subscriptionPrice < this.originalPrice) {
        // Subscription adds additional discount on top of sale
        displayPrice = subscriptionPrice;
        displayComparePrice = this.originalPrice; // Show sale price as compare
        showAsSale = true;
      } else if (!planChangesPrice) {
        // Subscription price is same as variant price - plan doesn't change the price
        displayPrice = this.originalPrice; // Keep showing variant sale price
        displayComparePrice = this.originalCompareAtPrice; // Keep showing original compare price
        showAsSale = true;
      } else {
        // Subscription price is higher than variant price
        displayPrice = subscriptionPrice; // Show the subscription price
        displayComparePrice = this.originalCompareAtPrice; // Show original compare price
        showAsSale = displayPrice < this.originalCompareAtPrice;
      }
    } else {
      // Product not on sale originally
      if (subscriptionPrice < this.originalPrice) {
        // Subscription adds discount
        displayPrice = subscriptionPrice;
        displayComparePrice = this.originalPrice;
        showAsSale = true;
      } else if (!planChangesPrice) {
        // Subscription price is same as variant price - no discount
        displayPrice = this.originalPrice; // Keep showing original price
        displayComparePrice = null; // No compare price
        showAsSale = false;
      } else {
        // Subscription price is higher than variant price
        displayPrice = subscriptionPrice; // Show subscription price
        displayComparePrice = null;
        showAsSale = false;
      }
    }

    this.updatePriceElements(displayPrice, displayComparePrice, showAsSale);

    const buttons = this.container.querySelectorAll('[data-add-to-cart]');
    buttons.forEach((button) => {
      if (sellingPlanId) {
        button.dataset.subscriptionPrice = displayPrice.toString();
        button.dataset.subscriptionCompareAtPrice = displayComparePrice ? displayComparePrice.toString() : '';
        button.dataset.sellingPlanId = sellingPlanId;
        button.dataset.sellingPlanAdjustment = sellingPlanAdjustment || '';
      } else {
        delete button.dataset.subscriptionPrice;
        delete button.dataset.subscriptionCompareAtPrice;
        delete button.dataset.sellingPlanId;
        delete button.dataset.sellingPlanAdjustment;
      }
    });
  }

  getQuantity() {
    const quantityInput = this.container.querySelector('[data-quantity-field]');
    if (!quantityInput) return 1;
    return parseInt(quantityInput.value || '1', 10) || 1;
  }

  getCachedPriceElements() {
    // Return cached elements if available, otherwise query and cache them
    if (!this.cachedPriceElements) {
      const priceWrappers = this.container.querySelectorAll('[data-price-wrapper]');
      const elements = [];

      priceWrappers.forEach((wrapper) => {
        const priceElements = wrapper.querySelectorAll('[data-product-price]');
        const comparePriceElements = wrapper.querySelectorAll('[data-compare-price]');

        elements.push({
          wrapper,
          priceElements: Array.from(priceElements),
          comparePriceElements: Array.from(comparePriceElements),
        });
      });

      this.cachedPriceElements = elements;
    }

    return this.cachedPriceElements;
  }

  invalidatePriceElementsCache() {
    // Called when product-info.js replaces DOM on variant change
    this.cachedPriceElements = null;
  }

  updatePriceElements(priceCents, compareCents, showAsSale) {
    // Use cached selectors - only query fresh when variant changes (cache is invalidated)
    const cachedElements = this.getCachedPriceElements();
    const quantity = this.getQuantity();
    const freeText = window.theme?.strings?.free;

    cachedElements.forEach(({wrapper, priceElements, comparePriceElements}) => {
      const isAtcButton = wrapper.hasAttribute('data-atc-button');
      const finalPriceCents = isAtcButton ? priceCents * quantity : priceCents;
      const finalCompareCents = compareCents != null ? (isAtcButton ? compareCents * quantity : compareCents) : null;

      const formattedPrice = finalPriceCents === 0 && freeText ? freeText : this.formatMoney(finalPriceCents);
      const formattedCompare = finalCompareCents != null ? this.formatMoney(finalCompareCents) : null;

      priceElements.forEach((priceEl) => {
        // Use innerHTML like product-info.js does for consistency
        priceEl.innerHTML = formattedPrice;

        // Add/remove sale class based on state
        if (showAsSale && formattedCompare) {
          priceEl.classList.add('product__price--sale');
        } else {
          priceEl.classList.remove('product__price--sale');
        }
      });

      if (showAsSale && formattedCompare) {
        // Show compare price
        comparePriceElements.forEach((compareEl) => {
          // Use innerHTML like product-info.js does
          compareEl.innerHTML = formattedCompare;
          // Use hidden class like product-info.js does for consistency
          compareEl.classList.remove('hidden');
          compareEl.style.display = ''; // Also clear any inline styles
        });
      } else {
        // Hide compare price
        comparePriceElements.forEach((compareEl) => {
          compareEl.classList.add('hidden');
        });
      }
    });
  }

  dispatchCustomEvent(radioButton) {
    const event = new CustomEvent('subscription:planChanged', {
      bubbles: true,
      detail: {
        price: radioButton.dataset.variantPrice,
        compareAtPrice: radioButton.dataset.variantCompareAtPrice,
        sellingPlanId: radioButton.dataset.sellingPlanId,
        sellingPlanAdjustment: radioButton.dataset.sellingPlanAdjustment,
        isProductOnSale: this.isProductOnSale,
        originalPrice: this.originalPrice,
        originalCompareAtPrice: this.originalCompareAtPrice,
        radioButton: radioButton,
      },
    });

    this.container.dispatchEvent(event);
  }

  disconnectSellingPlanObservers() {
    // Disconnect all selling plan observers
    this.sellingPlanObservers.forEach((observer) => {
      observer.disconnect();
    });
    this.sellingPlanObservers = [];
  }

  destroy() {
    this.container.removeEventListener('theme:variant:prices-updated', this._onVariantPricesUpdated);
    this.disconnectSellingPlanObservers();
    this.invalidatePriceElementsCache();
  }

  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) {
      console.error('formatMoney received invalid value:', cents);
      return '$0.00';
    }

    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    }

    // Fallback formatting using Intl API with dynamic currency
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: theme.current_iso_code,
      }).format(cents / 100);
    } catch (error) {
      console.error('Intl.NumberFormat error:', error);
      // Final fallback if Intl fails
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }
  }
}

// Store instances for cleanup
const subscriptionUpdaterInstances = new WeakMap();

// Initialize for all product sections
function initSubscriptionPriceUpdaters() {
  // Target product sections and forms
  const containers = document.querySelectorAll('[data-section-type*="product"]');

  containers.forEach((container) => {
    // Avoid duplicate initialization
    if (container.dataset.subscriptionPriceUpdaterInitialized) {
      return;
    }
    container.dataset.subscriptionPriceUpdaterInitialized = 'true';

    const instance = new SubscriptionPriceUpdater(container);

    // Store instance for potential cleanup
    subscriptionUpdaterInstances.set(container, instance);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSubscriptionPriceUpdaters);
} else {
  initSubscriptionPriceUpdaters();
}

// Re-initialize when sections are loaded
document.addEventListener('shopify:section:load', () => {
  initSubscriptionPriceUpdaters();
});

// Clean up when sections are unloaded
document.addEventListener('shopify:section:unload', (event) => {
  const container = event.target;
  const instance = subscriptionUpdaterInstances.get(container);

  if (instance) {
    instance.destroy();
    subscriptionUpdaterInstances.delete(container);
    delete container.dataset.subscriptionPriceUpdaterInitialized;
  }
});

// Export for external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SubscriptionPriceUpdater;
}
